import { useEffect, useRef, useState, useCallback } from "react";
import { ArrowLeft, Gem, Clock, X, Gift, Rocket, Copy, Loader2, Pickaxe } from "lucide-react";
import { toast } from "sonner";
import { ballSnipeApi, adsApi } from "@/api/mist";
import AlixAdReel from "@/components/ads/AlixAdReel";

// Mist Miner — Tetris-style mining. Place single/double/triple pieces to fill
// broken rows; complete rows clear & collect hidden icons. 3 matching icons ->
// a reward. Ad gems feed the SAME 7-day target as Ball Snipe. Client-only physics.

const COLS = 7;
const ROWS = 11;
const AD_SECONDS = 30;
const AD_X_AT = 25;
const ICONS = ["💎", "⛏️", "🔶", "🪙", "💠"];   // 5 types → matching 3 of one is a real grind
const ORES = ["#b45309", "#6b7280", "#2563eb", "#059669", "#dc2626", "#7c3aed"];
const ADS = [
  { headline: "Dig deeper. Win bigger.", sub: "Everything Misteck gives you 👇", items: [
    { t: "🎬 Mist Video Editor", d: "Trim, stylize & clean audio — free, on-device." },
    { t: "🪄 AI Studios", d: "Images, music, voiceovers & reels with ALIX." },
    { t: "💎 Match 3 icons", d: "Unlock a premium creator tool free." }]},
  { headline: "Turn your clips into cash.", sub: "Create it. Post it. Get paid.", items: [
    { t: "💸 Creator Payouts", d: "Earn from your reels across the Mist feed." },
    { t: "🎮 Two Mist games", d: "Ball Snipe + Tetminer — bank gems for rewards." },
    { t: "🏷️ Promote", d: "Burn your link onto reels and drive traffic." }]},
];

const rnd = (a) => a[Math.floor(Math.random() * a.length)];
const brokenRow = () => {
  const row = Array.from({ length: COLS }, () => (Math.random() < 0.55 ? { c: rnd(ORES), icon: null } : null));
  if (Math.random() < 0.06) {   // rare: reward icons show up seldom → ~2-3h to match 3 of a kind
    const filled = row.map((v, i) => (v ? i : -1)).filter((i) => i >= 0);
    if (filled.length) row[rnd(filled)].icon = rnd(ICONS);
  }
  return row;
};
const emptyRow = () => Array.from({ length: COLS }, () => null);
const randPiece = () => ({ w: rnd([1, 2, 3, 4]), c: rnd(ORES) });   // 1/2/3/4-block pieces
const zeroIcons = () => ICONS.reduce((a, k) => ({ ...a, [k]: 0 }), {});

export default function MistMiner({ onBack }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const st = useRef(null);
  const [, force] = useState(0);
  const rerender = () => force((n) => n + 1);

  const [depth, setDepth] = useState(0);
  const [icons, setIcons] = useState(zeroIcons);
  const [tray, setTray] = useState([randPiece(), randPiece(), randPiece()]);
  const [over, setOver] = useState(false);
  const [hud, setHud] = useState({ gems: 0, target: 200, seconds_left: 0 });
  const [ad, setAd] = useState(null);
  const [reward, setReward] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const adTimer = useRef(null);

  const refreshState = useCallback(async () => {
    try { const s = await ballSnipeApi.state(); setHud(s); if (s.pending_reward) setReward({ pool: s.reward_pool }); } catch { /* ok */ }
  }, []);
  useEffect(() => { refreshState(); }, [refreshState]);

  const reset = useCallback(() => {
    const board = Array.from({ length: ROWS }, emptyRow);
    for (let r = ROWS - 3; r < ROWS; r++) board[r] = brokenRow();
    st.current = { board, lastAdDepth: 0, noClear: 0 };
    setDepth(0); setIcons(zeroIcons());
    setTray([randPiece(), randPiece(), randPiece()]); setOver(false);
    rerender();
  }, []);
  useEffect(() => { reset(); }, [reset]);

  const openAd = async (ctx, dep) => {
    let qr = "", link = "";
    try { const r = await ballSnipeApi.qr(); qr = r.qr; link = r.link; } catch { /* opt */ }
    let sponsor = null;
    try { const s = await adsApi.serveGame(); if (s.slot) sponsor = s.slot; } catch { /* none */ }
    setAd({ ctx, dep, remain: AD_SECONDS, canClose: false, qr, link, creative: rnd(ADS), sponsor });
    clearInterval(adTimer.current);
    adTimer.current = setInterval(() => setAd((a) => a ? { ...a, remain: Math.max(0, a.remain - 1), canClose: (a.remain - 1) <= AD_SECONDS - AD_X_AT } : a), 1000);
  };
  const closeAd = async () => {
    const a = ad; clearInterval(adTimer.current); setAd(null);
    try { const res = await ballSnipeApi.adReward(a?.dep || depth, "level_gate"); setHud(res); toast.success(`+${res.gems_added} 💎 gems!`); if (res.reward_ready) setReward({ pool: res.reward_pool }); }
    catch { /* ok */ }
  };
  useEffect(() => () => clearInterval(adTimer.current), []);

  const bankIconReward = async () => {
    try {
      const r = await ballSnipeApi.rewardNow();
      if (r.reward_ready || r.already_pending) { setReward({ pool: r.reward_pool }); toast.success("3 icons matched — reward unlocked! 🎁"); }
      else if (r.cooldown) { const h = Math.ceil((r.seconds_left || 0) / 3600); toast(`Reward cooling down — back in ~${h}h. Keep digging!`, { icon: "⛏️" }); }
    } catch { /* ok */ }
  };

  // Gems collect in small random amounts (1-5, 5 rare). After a cave-in you must
  // climb back to your best depth before full gems resume (server-enforced).
  const collectGems = useCallback(async (lvl, events) => {
    try {
      const r = await ballSnipeApi.collect(lvl, "tetminer", events);
      setHud(r);
      if (r.gems_added > 0) toast.success(`+${r.gems_added} 💎`, { duration: 1100 });
      if (r.reward_ready) setReward({ pool: r.reward_pool });
    } catch { /* ok */ }
  }, []);

  const resolve = useCallback(() => {
    const e = st.current; const b = e.board;
    const collected = [];
    const full = [];
    for (let r = 0; r < ROWS; r++) if (b[r].every((c) => c)) full.push(r);
    const cleared = full.length;
    if (cleared) {
      full.forEach((r) => b[r].forEach((c) => { if (c && c.icon) collected.push(c.icon); }));
      full.forEach((r) => { for (let c = 0; c < COLS; c++) b[r][c] = null; });
      // Column gravity — blocks above a cleared line fall straight down to fill
      // any holes they can settle into (not just a whole-board shift).
      for (let c = 0; c < COLS; c++) {
        const stack = [];
        for (let r = 0; r < ROWS; r++) if (b[r][c]) stack.push(b[r][c]);
        const start = ROWS - stack.length;
        for (let r = 0; r < ROWS; r++) b[r][c] = r >= start ? stack[r - start] : null;
      }
      e.noClear = 0;
      const nd = depth + cleared;
      setDepth(nd);
      collectGems(nd, cleared);
      if (nd - e.lastAdDepth >= 8) { e.lastAdDepth = nd; openAd("level_gate", nd); }
      if (collected.length) {
        setIcons((prev) => {
          const next = { ...prev };
          collected.forEach((ic) => { next[ic] = (next[ic] || 0) + 1; });
          Object.keys(next).forEach((ic) => { if (next[ic] >= 3) { next[ic] = 0; bankIconReward(); } });
          return next;
        });
      }
    } else {
      // Mining pressure: only every 3rd non-clearing move does a fresh broken row
      // rise from the bottom (keeps the board playable rather than instant game-over).
      e.noClear = (e.noClear || 0) + 1;
      if (e.noClear >= 3) {
        e.noClear = 0;
        if (b[0].some((c) => c)) { e.board = b; setOver(true); setIcons(zeroIcons()); rerender(); return; }
        b.shift(); b.push(brokenRow());
      }
    }
    rerender();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ad, depth, collectGems]);

  const drop = useCallback((colStart) => {
    const e = st.current; if (!e || over) return;
    const piece = tray[0]; if (!piece) return;
    const w = piece.w;
    const col = Math.max(0, Math.min(COLS - w, colStart));
    const b = e.board;
    let top = ROWS;
    for (let c = col; c < col + w; c++) { let f = ROWS; for (let r = 0; r < ROWS; r++) if (b[r][c]) { f = r; break; } top = Math.min(top, f); }
    const land = top - 1;
    if (land < 0) { setOver(true); setIcons(zeroIcons()); return; }
    for (let c = col; c < col + w; c++) b[land][c] = { c: piece.c, icon: null };
    setTray((t) => [t[1], t[2], randPiece()]);   // queue shifts left, new piece enters
    resolve();
  }, [tray, over, resolve]);

  // ---- render loop ----
  useEffect(() => {
    const cvs = canvasRef.current, wrap = wrapRef.current; if (!cvs || !wrap) return;
    const w = Math.min(wrap.clientWidth, 460); cvs.width = w; cvs.height = Math.max(360, wrap.clientHeight - 4);
    const ctx = cvs.getContext("2d");
    let raf;
    const draw = () => {
      const e = st.current; if (!e) { raf = requestAnimationFrame(draw); return; }
      const W = cvs.width, H = cvs.height, cell = W / COLS, top = 8;
      ctx.fillStyle = "#2a3358"; ctx.fillRect(0, 0, W, H);   // lighter slate board
      // danger line
      ctx.strokeStyle = "rgba(239,68,68,0.6)"; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(0, top + cell - 2); ctx.lineTo(W, top + cell - 2); ctx.stroke();
      for (let r = 0; r < ROWS; r++) for (let c = 0; c < COLS; c++) {
        const x = c * cell + 2, y = top + r * cell + 2, s = cell - 4;
        const v = e.board[r][c];
        if (!v) { ctx.strokeStyle = "rgba(255,255,255,0.09)"; ctx.lineWidth = 1; ctx.strokeRect(x, y, s, s); continue; }
        ctx.fillStyle = v.c; ctx.beginPath(); ctx.roundRect(x, y, s, s, 5); ctx.fill();
        ctx.fillStyle = "rgba(255,255,255,0.15)"; ctx.fillRect(x, y, s, 3);
        if (v.icon) { ctx.font = `${Math.floor(cell * 0.5)}px serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle"; ctx.fillText(v.icon, x + s / 2, y + s / 2 + 1); }
      }
      // ghost preview of the piece being slid into place
      const gh = e.ghost;
      if (gh) {
        let tp = ROWS;
        for (let c = gh.col; c < gh.col + gh.w; c++) { let f = ROWS; for (let r = 0; r < ROWS; r++) if (e.board[r][c]) { f = r; break; } tp = Math.min(tp, f); }
        const ly = Math.max(0, tp - 1);
        for (let c = gh.col; c < gh.col + gh.w; c++) {
          const x = c * cell + 2, y = top + ly * cell + 2, s = cell - 4;
          ctx.globalAlpha = 0.5; ctx.fillStyle = gh.color; ctx.beginPath(); ctx.roundRect(x, y, s, s, 5); ctx.fill(); ctx.globalAlpha = 1;
          ctx.strokeStyle = "#ffffff"; ctx.lineWidth = 2; ctx.strokeRect(x, y, s, s);
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  const onDown = (ev) => {
    const e = st.current; if (!e || over) return;
    const piece = tray[0]; if (!piece) return;
    const cvs = canvasRef.current; const rect = cvs.getBoundingClientRect();
    const x = (ev.clientX - rect.left) * (cvs.width / rect.width);
    const col = Math.max(0, Math.min(COLS - piece.w, Math.floor(x / (cvs.width / COLS))));
    e.ghost = { col, w: piece.w, color: piece.c };
    e.dragging = true;
  };
  const onMove = (ev) => {
    const e = st.current; if (!e || !e.dragging || !e.ghost) return;
    ev.preventDefault();
    const cvs = canvasRef.current; const rect = cvs.getBoundingClientRect();
    const x = (ev.clientX - rect.left) * (cvs.width / rect.width);
    e.ghost.col = Math.max(0, Math.min(COLS - e.ghost.w, Math.floor(x / (cvs.width / COLS))));
  };
  const onUp = () => {
    const e = st.current; if (!e || !e.ghost) return;
    const col = e.ghost.col; e.ghost = null; e.dragging = false;
    drop(col);
  };

  const claim = async (feature) => {
    setClaiming(true);
    try { const r = await ballSnipeApi.claimReward(feature); toast.success(r.message || "Reward unlocked!"); setReward(null); refreshState(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't claim"); }
    finally { setClaiming(false); }
  };

  const days = Math.floor((hud.seconds_left || 0) / 86400);
  const hrs = Math.floor(((hud.seconds_left || 0) % 86400) / 3600);

  return (
    <div className="absolute inset-0 z-30 flex flex-col bg-[#232a44]" data-testid="mist-miner">
      <div className="flex items-center gap-2 border-b-2 border-white/10 px-3 py-2.5">
        <button onClick={onBack} data-testid="mist-miner-back" className="grid h-9 w-9 place-items-center rounded-xl border-2 border-white/30 bg-white/5 active:scale-95"><ArrowLeft size={16} className="text-white" /></button>
        <div className="flex items-center gap-1.5 rounded-lg border-2 border-violet-400/40 bg-violet-500/10 px-2 py-1" data-testid="mist-miner-gems">
          <Gem size={14} className="text-violet-300" /><span className="font-mono text-sm font-black text-white">{hud.gems}</span><span className="font-mono text-[9px] text-white/40">/ {hud.target}</span>
        </div>
        <div className="flex items-center gap-1 rounded-lg border-2 border-white/15 px-2 py-1"><Clock size={12} className="text-white/50" /><span className="font-mono text-[10px] font-bold text-white/70">{days}d {hrs}h</span></div>
        <div className="ml-auto flex items-center gap-1.5 rounded-lg border-2 border-gold/40 bg-gold/10 px-2 py-1" data-testid="mist-miner-icons">
          {ICONS.map((ic) => <span key={ic} className="font-mono text-[11px] text-white">{ic}{icons[ic] || 0}</span>)}
        </div>
      </div>

      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/5">
        <Pickaxe size={13} className="text-amber-400" />
        <span className="font-mono text-[10px] font-black uppercase tracking-widest text-amber-300" data-testid="mist-miner-depth">Tetminer · Depth −{depth * 40}m · Lv {depth + 1}</span>
      </div>

      <div ref={wrapRef} className="relative flex-1 min-h-0 flex items-stretch justify-center p-1">
        <canvas ref={canvasRef} data-testid="mist-miner-canvas" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} className="rounded-xl border-2 border-white/10 w-full touch-none" style={{ maxWidth: 460 }} />
      </div>

      {/* Piece tray — only the LEFT piece is live; the other two are up-next previews */}
      <div className="flex items-center gap-2 border-t-2 border-white/10 px-3 py-3" data-testid="mist-miner-tray">
        <span className="font-mono text-[8px] uppercase tracking-widest text-gold">Now</span>
        <div data-testid="mist-miner-piece-0" className="flex items-center gap-0.5 rounded-lg border-2 border-gold bg-gold/15 p-2">
          {Array.from({ length: tray[0]?.w || 1 }).map((_, k) => <span key={k} className="h-4 w-4 rounded-sm" style={{ backgroundColor: tray[0]?.c }} />)}
        </div>
        <span className="ml-2 font-mono text-[8px] uppercase tracking-widest text-white/30">Next</span>
        {[1, 2].map((i) => (
          <div key={i} data-testid={`mist-miner-piece-${i}`} className="flex items-center gap-0.5 rounded-lg border-2 border-white/10 bg-white/5 p-2 opacity-40">
            {Array.from({ length: tray[i]?.w || 1 }).map((_, k) => <span key={k} className="h-3.5 w-3.5 rounded-sm" style={{ backgroundColor: tray[i]?.c }} />)}
          </div>
        ))}
        <span className="ml-auto font-mono text-[8px] uppercase tracking-widest text-white/40">Hold & drag to place</span>
      </div>

      {/* GAME OVER */}
      {over && (
        <div className="absolute inset-0 z-40 grid place-items-center bg-black/85 p-6" data-testid="mist-miner-gameover">
          <div className="w-full max-w-[320px] rounded-2xl border-2 border-white/20 bg-[#141a2e] p-5 text-center">
            <p className="font-serif text-2xl text-white">The mine caved in! ⛏️</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/40">Reached level {depth + 1} · best {hud.best_level || 0}</p>
            <button onClick={reset} data-testid="mist-miner-restart" className="mt-4 w-full rounded-xl border-2 border-amber-400 bg-amber-500 py-3 font-mono text-[11px] font-black uppercase tracking-widest text-black active:scale-[0.98]">Dig again — Level 1</button>
            <p className="mt-3 font-mono text-[8px] uppercase tracking-widest text-white/30">Gems only trickle until you climb back to level {hud.best_level || 0}</p>
          </div>
        </div>
      )}

      {/* SITE AD (shared style) */}
      {ad && (
        <div className="absolute inset-0 z-50 flex flex-col bg-gradient-to-b from-[#1a1030] to-[#0a0f1e]" data-testid="mist-miner-ad">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10">
            <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-violet-300">Sponsored · Misteck</span>
            <span className="ml-auto font-mono text-[11px] font-black text-white/70" data-testid="mist-miner-ad-timer">{ad.remain}s</span>
            {ad.canClose && <button onClick={closeAd} data-testid="mist-miner-ad-close" className="grid h-8 w-8 place-items-center rounded-lg border-2 border-white/40 bg-white/10 active:scale-95"><X size={16} className="text-white" /></button>}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
            {ad.sponsor && (
              <button onClick={() => { adsApi.click(ad.sponsor.id, 'game').then((r) => { if (r?.target_url) window.open(r.target_url, "_blank"); }); }}
                data-testid="mist-miner-sponsor" className="mb-4 block w-full overflow-hidden rounded-2xl border-2 border-gold/50 bg-black/40 text-left">
                {ad.sponsor.media_kind === "video" ? (
                  <video src={`${process.env.REACT_APP_BACKEND_URL}/api/ads/media/${ad.sponsor.id}`} autoPlay muted loop playsInline className="w-full object-cover max-h-44" />
                ) : (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/ads/media/${ad.sponsor.id}`} alt={ad.sponsor.title} className="w-full object-cover max-h-44" />
                )}
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="font-serif text-sm text-white">{ad.sponsor.title}</span>
                  <span className="font-mono text-[8px] uppercase tracking-widest text-gold">Learn more →</span>
                </div>
              </button>
            )}
            <h2 className="font-serif text-3xl text-white leading-tight">{ad.creative?.headline}</h2>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-violet-300/80">{ad.creative?.sub}</p>
            <div className="mt-4 space-y-2.5">{(ad.creative?.items || []).map((f) => (
              <div key={f.t} className="rounded-xl border-2 border-white/10 bg-white/5 p-3"><p className="font-serif text-base text-white">{f.t}</p><p className="mt-0.5 font-mono text-[10px] leading-snug text-white/60">{f.d}</p></div>
            ))}</div>
            {!ad.sponsor && <AlixAdReel onJoin={closeAd} className="mt-4" />}
            {ad.canClose ? (
              <div className="mt-5 rounded-2xl border-2 border-gold/40 bg-black/40 p-4 text-center" data-testid="mist-miner-ad-qr">
                <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Invite a friend · screenshot this QR</p>
                {ad.qr ? <img src={ad.qr} alt="Referral QR" className="mx-auto mt-3 h-40 w-40 rounded-lg bg-white p-2" /> : <div className="mx-auto mt-3 grid h-40 w-40 place-items-center rounded-lg bg-white/10"><Loader2 className="animate-spin text-white/40" /></div>}
                {ad.link && <button onClick={() => { navigator.clipboard?.writeText(ad.link); toast.success("Link copied"); }} className="mt-3 inline-flex items-center gap-1.5 rounded-lg border-2 border-white/25 bg-white/5 px-3 py-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-white/80"><Copy size={11} /> Copy link</button>}
              </div>
            ) : <p className="mt-5 text-center font-mono text-[9px] uppercase tracking-widest text-white/30">📸 Your invite QR appears in the last {AD_SECONDS - AD_X_AT}s…</p>}
          </div>
        </div>
      )}

      {/* REWARD */}
      {reward && !ad && (
        <div className="absolute inset-0 z-50 grid place-items-center bg-black/85 p-6" data-testid="mist-miner-reward">
          <div className="w-full max-w-[340px] rounded-2xl border-2 border-gold/60 bg-[#141a2e] p-5 text-center">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl border-2 border-gold bg-gold/15"><Gift size={26} className="text-gold" /></div>
            <h3 className="mt-3 font-serif text-2xl text-white">Reward unlocked! 🎉</h3>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/50">Pick 1 tool — one free use</p>
            <div className="mt-4 space-y-2">{(reward.pool || []).map((tool) => (
              <button key={tool.feature} onClick={() => claim(tool.feature)} disabled={claiming} data-testid={`mist-miner-claim-${tool.feature}`}
                className="w-full flex items-center gap-2 rounded-xl border-2 border-white/20 bg-white/5 px-4 py-3 text-left active:scale-[0.98] disabled:opacity-50">
                <Rocket size={15} className="text-gold shrink-0" /><span className="flex-1 font-mono text-[11px] font-black uppercase tracking-wide text-white">{tool.label}</span>
                {claiming ? <Loader2 size={14} className="animate-spin text-white/50" /> : <span className="font-mono text-[8px] text-white/40">1× free</span>}
              </button>
            ))}</div>
            <button onClick={() => setReward(null)} data-testid="mist-miner-reward-later" className="mt-3 font-mono text-[9px] uppercase tracking-widest text-white/40">Maybe later</button>
          </div>
        </div>
      )}
    </div>
  );
}
